import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building, MapPin, Pencil, Plus, Trees, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Ruralidad, Sector, SectorConCatastro } from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { notificar } from '@/shared/ui/avisos';

export function PaginaParroquias() {
  const clienteDeConsultas = useQueryClient();

  const [editando, setEditando] = useState<SectorConCatastro | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<SectorConCatastro | null>(null);
  const [error, setError] = useState<string | null>(null);

  const parroquias = useQuery({
    queryKey: ['parroquias'],
    queryFn: () => api.get<SectorConCatastro[]>('/sectores/catastro'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['parroquias'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['sectores'] });
  }

  async function eliminar(s: SectorConCatastro) {
    try {
      await api.delete<void>(`/sectores/${s.id}`);
      setAEliminar(null);
      refrescar();
      notificar.exito(`Parroquia eliminada: ${s.parroquia}`);
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo eliminar la parroquia';
      setError(mensaje);
      notificar.error(mensaje);
    }
  }

  const filas = parroquias.data ?? [];
  const urbanas = filas.filter((s) => s.ruralidad === 'urbano');
  const rurales = filas.filter((s) => s.ruralidad === 'rural');
  const sinCodigo = filas.filter((s) => !s.codparr).length;

  return (
    <>
      <EncabezadoDePagina
        icono={MapPin}
        titulo="Parroquias"
        subtitulo="DÓNDE VIVE CADA CLIENTE Y, POR TANTO, QUÉ TARIFA PAGA"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nueva parroquia"
            aria-label="Nueva parroquia"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Indicador icono={Building} valor={urbanas.length} rotulo="Urbanas" />
        <Indicador icono={Trees} valor={rurales.length} rotulo="Rurales" />
        <Indicador
          icono={MapPin}
          valor={filas.reduce((suma, s) => suma + s.clientes, 0)}
          rotulo="Clientes ubicados"
        />
        <Indicador
          icono={AlertTriangle}
          valor={sinCodigo}
          rotulo="Sin código del INEC"
          alerta={sinCodigo > 0}
        />
      </div>

      {sinCodigo > 0 && (
        <div className="mb-5 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
          <p className="text-xs uppercase leading-relaxed text-amber-800">
            <span className="font-semibold">
              {sinCodigo} {sinCodigo === 1 ? 'parroquia no tiene' : 'parroquias no tienen'} código
              del INEC.
            </span>{' '}
            Vienen así del sistema anterior, donde se creaban solas al importar el catastro y ese
            campo nunca se llenaba. Hace falta para cualquier reporte que se cruce con datos
            oficiales.
          </p>
        </div>
      )}

      {parroquias.isPending ? (
        <Cargando titulo="Cargando las parroquias" detalle="Trayendo el catálogo del INEC" alto="h-96" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
          <div className="hidden items-center gap-4 border-b border-armazon-100 bg-armazon-50/60 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400 lg:flex">
            <span className="w-8 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 basis-64">Parroquia</span>
            <span className="w-28 shrink-0">Código INEC</span>
            <span className="w-24 shrink-0 text-right">Clientes</span>
            <span className="w-24 shrink-0 text-right">Predios</span>
            <span className="w-16 shrink-0" aria-hidden />
          </div>

          <div className="divide-y divide-armazon-100">
            {filas.map((s) => (
              <div
                key={s.id}
                className="flex flex-wrap items-center gap-4 px-6 py-4 transition-colors hover:bg-armazon-50/60"
              >
                <span
                  className={`grid size-8 shrink-0 place-items-center rounded-lg ${
                    s.ruralidad === 'urbano'
                      ? 'bg-indigo-50 text-indigo-500'
                      : 'bg-emerald-50 text-emerald-600'
                  }`}
                  title={s.ruralidad === 'urbano' ? 'Parroquia urbana' : 'Parroquia rural'}
                >
                  {s.ruralidad === 'urbano' ? (
                    <Building className="size-3.5" aria-hidden />
                  ) : (
                    <Trees className="size-3.5" aria-hidden />
                  )}
                </span>

                <div className="min-w-0 flex-1 basis-64">
                  <p className="truncate text-sm font-medium uppercase text-armazon-800">
                    {s.parroquia}
                  </p>

                  <span className="mt-0.5 block text-[10px] uppercase text-armazon-400">
                    {s.ruralidad === 'urbano' ? 'Urbana' : 'Rural'} · tarifa{' '}
                    {s.ruralidad === 'urbano' ? 'urbana' : 'rural'}
                  </span>
                </div>

                <p className="importe w-28 shrink-0 text-xs text-armazon-600">
                  {s.codparr ?? <span className="uppercase text-armazon-300">Sin código</span>}
                </p>

                <p className="importe w-24 shrink-0 text-right text-sm font-semibold text-armazon-800">
                  {s.clientes.toLocaleString('es-EC')}
                </p>

                <p className="importe w-24 shrink-0 text-right text-sm text-armazon-600">
                  {s.cuentas.toLocaleString('es-EC')}
                </p>

                <div className="flex w-16 shrink-0 items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => setEditando(s)}
                    title="Editar"
                    aria-label={`Editar ${s.parroquia}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Pencil className="size-[15px]" aria-hidden />
                  </button>

                  {s.clientes === 0 && (
                    <button
                      type="button"
                      onClick={() => setAEliminar(s)}
                      title="Eliminar"
                      aria-label={`Eliminar ${s.parroquia}`}
                      className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
                    >
                      <Trash2 className="size-[15px]" aria-hidden />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogoDeParroquia
        key={editando?.id ?? 'nueva'}
        abierto={creando || editando !== null}
        parroquia={editando}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        titulo="Eliminar parroquia"
        descripcion={
          aEliminar === null
            ? ''
            : `Se eliminará «${aEliminar.parroquia}». No tiene ningún cliente asignado.`
        }
        alCerrar={() => setAEliminar(null)}
        alConfirmar={() => aEliminar && void eliminar(aEliminar)}
        eliminando={false}
        error={error}
      />
    </>
  );
}

function Indicador({
  icono: Icono,
  valor,
  rotulo,
  alerta = false,
}: {
  icono: typeof MapPin;
  valor: number;
  rotulo: string;
  alerta?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border bg-white p-4 ${
        alerta ? 'border-amber-200' : 'border-armazon-200'
      }`}
    >
      <span
        className={`mb-2 grid size-8 place-items-center rounded-lg ${
          alerta ? 'bg-amber-50 text-amber-600' : 'bg-armazon-100 text-armazon-500'
        }`}
      >
        <Icono className="size-4" aria-hidden />
      </span>
      <span
        className={`importe block text-2xl font-bold ${
          alerta ? 'text-amber-600' : 'text-armazon-800'
        }`}
      >
        {valor.toLocaleString('es-EC')}
      </span>
      <span className="block text-[10px] font-medium uppercase text-armazon-400">{rotulo}</span>
    </div>
  );
}

function DialogoDeParroquia({
  abierto,
  parroquia,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  parroquia: SectorConCatastro | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = parroquia !== null;

  const [nombre, setNombre] = useState(parroquia?.parroquia ?? '');
  const [canton, setCanton] = useState(parroquia?.canton ?? 'Latacunga');
  const [provincia, setProvincia] = useState(parroquia?.provincia ?? 'Cotopaxi');
  const [codparr, setCodparr] = useState(parroquia?.codparr ?? '');
  const [ruralidad, setRuralidad] = useState<Ruralidad>(parroquia?.ruralidad ?? 'urbano');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const listo = nombre.trim() !== '' && canton.trim() !== '' && provincia.trim() !== '';
  const cambiaLaRuralidad = editando && ruralidad !== parroquia.ruralidad;

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const cuerpo = {
        parroquia: nombre.trim(),
        canton: canton.trim(),
        provincia: provincia.trim(),
        codparr: codparr.trim() || null,
        ruralidad,
      };

      if (editando) {
        await api.put<Sector>(`/sectores/${parroquia.id}`, cuerpo);
      } else {
        await api.post<Sector>('/sectores', cuerpo);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Parroquia ${editando ? 'actualizada' : 'creada'}: ${cuerpo.parroquia}`);
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la parroquia';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar parroquia' : 'Nueva parroquia'}
      descripcion={editando ? parroquia.parroquia : 'Una división territorial del cantón'}
      icono={MapPin}
      alEnviar={enviar}
      enviando={enviando || !listo}
    >
      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <CampoDelPanel
        etiqueta="Parroquia"
        requerido
        ayuda="COMO CONSTA EN LA DIVISIÓN TERRITORIAL DEL CANTÓN"
      >
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          className={claseEntradaPanel}
          placeholder="SAN JUAN DE PASTOCALLE"
          autoFocus
        />
      </CampoDelPanel>

      <DosColumnas>
        <CampoDelPanel etiqueta="Cantón" requerido>
          <input
            value={canton}
            onChange={(e) => setCanton(e.target.value)}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Provincia" requerido>
          <input
            value={provincia}
            onChange={(e) => setProvincia(e.target.value)}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>
      </DosColumnas>

      <DosColumnas>
        <CampoDelPanel
          etiqueta="Código del INEC"
          ayuda="OPCIONAL. HACE FALTA PARA CRUZAR REPORTES CON DATOS OFICIALES."
        >
          <input
            value={codparr}
            onChange={(e) => setCodparr(e.target.value)}
            className={`${claseEntradaPanel} importe`}
            placeholder="050150"
          />
        </CampoDelPanel>

        <CampoDelPanel
          etiqueta="Ruralidad"
          requerido
          ayuda="DECIDE QUÉ TARIFA DEL ART. 9 SE APLICA A SUS PREDIOS"
        >
          <select
            value={ruralidad}
            onChange={(e) => setRuralidad(e.target.value as Ruralidad)}
            className={claseEntradaPanel}
          >
            <option value="urbano">URBANA</option>
            <option value="rural">RURAL</option>
          </select>
        </CampoDelPanel>
      </DosColumnas>

      {cambiaLaRuralidad && parroquia.clientes > 0 && (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
          <p className="text-xs uppercase leading-relaxed text-amber-800">
            <span className="font-semibold">
              Está cambiando la ruralidad de una parroquia con{' '}
              {parroquia.clientes.toLocaleString('es-EC')} clientes.
            </span>{' '}
            Sus predios pasarán a cobrarse con la otra tarifa del Art. 9 en la próxima emisión.
          </p>
        </div>
      )}
    </PanelLateral>
  );
}
